/**
 * This component is a low-level utility component for creating links.
 * It detects whether the link is internal or external and uses the correct
 * element (either an <a> tag or a <Link> from React Router).
 *
 * This component should only be used as a building block for other components,
 * or when you need to turn a large chunk of HTML into a link without changing
 * the styling.
 */
import * as React from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import styled, { css } from "styled-components";
import { focusOutline, linkFocusOutline } from "./utils/focusOutlineStyle";
import { shouldUseAnchor } from "./utils/shouldUseAnchor";

type AnchorLinkProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  underline?: boolean;
  $enableLineBreak?: boolean;
};

const AnchorLink = styled.a<AnchorLinkProps>`
  ${({ underline }) =>
    underline &&
    css`
      text-decoration: underline;
      &:hover {
        text-decoration: none;
      }
    `}

  ${({ $enableLineBreak }) =>
    $enableLineBreak
      ? linkFocusOutline()
      : css`
          &&:active,
          &&:focus {
            ${focusOutline()}
            text-decoration: none;
          }
        `}
`;

export type LinkProps = AnchorLinkProps & {
  to: string;
  children: React.ReactNode;
  forceExternal?: boolean;
  enableLineBreak?: boolean;
};

export const BaseLink: React.FC<LinkProps> = ({ to, children, forceExternal, enableLineBreak, ...delegated }) => {
  if (!to || shouldUseAnchor(to) || forceExternal) {
    let target = undefined;
    if (forceExternal) {
      target = "_blank";
    }

    return (
      <AnchorLink {...delegated} href={to} target={delegated.target || target} rel="noopener noreferrer" $enableLineBreak={enableLineBreak}>
        {children}
      </AnchorLink>
    );
  } else {
    return (
      <ReactRouterLink to={to} {...delegated}>
        {children}
      </ReactRouterLink>
    );
  }
};
